import {notEmpty} from '@wandb/weave/common/util/obj';
import {useDeepMemo} from '@wandb/weave/hookUtils';
import {useMemo} from 'react';

import {
  type RunSelectorCsvExportRunsQueryVariables,
  useRunSelectorCsvExportRunsQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filters from '../../../util/filters';
import {usePinnedRunFilters} from '../../../util/hooks/usePinnedRunsFilters';
import * as Run from '../../../util/runs';
import {type RunSelectorRunsPartialVars} from '../types';

const EXPORTED_RUNS_LIMIT = 10000;

export const useCsvExportRuns = (
  topLevelQueryVariables: RunSelectorRunsPartialVars
) => {
  const {
    filters,
    grouping,
    selections,
    baselineRunId,
    pinnedRunIds = [],
    ...passThroughVars
  } = topLevelQueryVariables;
  const passThroughVarsMemoized = useDeepMemo(passThroughVars);
  const {excludedIDsFilter} = usePinnedRunFilters(pinnedRunIds, baselineRunId);

  const queryVariables = useMemo(() => {
    const filtersWithSelections =
      selections != null ? Filters.And([filters, selections]) : filters;
    const filtersWithPinnedExclusion = excludedIDsFilter
      ? Filters.And([filtersWithSelections, excludedIDsFilter])
      : filtersWithSelections;
    const groupKeys = grouping?.map(Run.keyToServerPath) ?? [];

    const variables: RunSelectorCsvExportRunsQueryVariables = {
      ...passThroughVarsMemoized,
      filters: JSON.stringify(Filters.toMongo(filtersWithPinnedExclusion)),
      groupKeys,
      limit: EXPORTED_RUNS_LIMIT,
      pinnedRunIds,
    };
    return variables;
  }, [
    passThroughVarsMemoized,
    excludedIDsFilter,
    pinnedRunIds,
    filters,
    grouping,
    selections,
  ]);

  const {loading, data} = useRunSelectorCsvExportRunsQuery({
    context: propagateErrorsContext(),
    variables: queryVariables,
    fetchPolicy: 'no-cache',
  });

  const runs = useMemo(() => {
    const pinned =
      data?.pinnedRuns?.runs?.map(run => Run.fromJson(run)).filter(notEmpty) ??
      [];
    const nonPinned =
      data?.project?.runs?.edges
        ?.map(({node}) => Run.fromJson(node))
        .filter(notEmpty) ?? [];
    return [...pinned, ...nonPinned];
  }, [data?.pinnedRuns?.runs, data?.project?.runs?.edges]);

  return {
    isLoading: loading,
    runs,
  };
};
